// tabellenIcons.tsx — die Symbole im Tabellenkopf der DataTable-Engine.
//
// Nutzer haben den Trichter als Filter-Symbol und die winzigen Unicode-Dreiecke
// („▴/▾/↕") als Sortier-Anzeige nicht verstanden. Deshalb:
//   - Filter = Lupe (wie die Suche, die alle kennen),
//   - Sortierung = zwei deutliche Pfeile übereinander (hoch/runter, ~20 px),
//     der aktive Pfeil ist kräftig, der andere tritt zurück.
// Dieselben Pfeile stehen im Rechtsklick-Menü der Spalte.

import * as React from "react";
import type { SortDirection } from "./DataTable";

const svgProps = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** Lupe — Symbol für „Spalte filtern". */
export const LupeIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" strokeWidth={1.9} {...svgProps} aria-hidden>
    <circle cx="8.6" cy="8.6" r="5.4" />
    <path d="M12.6 12.6 17 17" />
  </svg>
);

/**
 * Sortier-Anzeige im Spaltenkopf: Pfeil hoch + Pfeil runter (20 px).
 * `dir` hebt den aktiven Pfeil hervor; ohne `dir` sind beide gedämpft.
 */
export function SortPfeile({ dir, size = 20 }: { dir?: SortDirection; size?: number }) {
  const hoch = dir === "asc" ? 1 : dir ? 0.3 : 0.7;
  const runter = dir === "desc" ? 1 : dir ? 0.3 : 0.7;
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" strokeWidth={2.1} {...svgProps} aria-hidden>
      <g opacity={hoch} data-pfeil="hoch">
        <path d="M6.5 15.5V4.5" />
        <path d="M3.4 7.6 6.5 4.5l3.1 3.1" />
      </g>
      <g opacity={runter} data-pfeil="runter">
        <path d="M13.5 4.5v11" />
        <path d="M10.4 12.4l3.1 3.1 3.1-3.1" />
      </g>
    </svg>
  );
}

/** Einzelner Pfeil hoch — „Aufsteigend sortieren" im Spaltenmenü. */
export const PfeilHochIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" strokeWidth={2} {...svgProps} aria-hidden>
    <path d="M10 16V4" />
    <path d="M5.5 8.5 10 4l4.5 4.5" />
  </svg>
);

/** Einzelner Pfeil runter — „Absteigend sortieren" im Spaltenmenü. */
export const PfeilRunterIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" strokeWidth={2} {...svgProps} aria-hidden>
    <path d="M10 4v12" />
    <path d="M5.5 11.5 10 16l4.5-4.5" />
  </svg>
);

/** Durchgestrichene Pfeile — „Sortierung aufheben". */
export const SortAufhebenIcon = (
  <svg width="18" height="18" viewBox="0 0 20 20" strokeWidth={1.9} {...svgProps} aria-hidden>
    <path d="M6.5 15.5V4.5M3.4 7.6 6.5 4.5l3.1 3.1" opacity={0.7} />
    <path d="M13.5 4.5v11M10.4 12.4l3.1 3.1 3.1-3.1" opacity={0.7} />
    <path d="M3.5 16.5 16.5 3.5" />
  </svg>
);

/** Chevron links/rechts — Ein-/Ausklappen im Spaltenmenü. */
export const ChevronIcon = ({ richtung }: { richtung: "links" | "rechts" }) => (
  <svg width="18" height="18" viewBox="0 0 20 20" strokeWidth={2} {...svgProps} aria-hidden>
    <path d={richtung === "rechts" ? "M8 5l5 5-5 5" : "M12 5l-5 5 5 5"} />
  </svg>
);
